import { useWatch, type Control } from 'react-hook-form'
import { useFinalidades, useModulos, usePessoas, useSistemas } from '../../../api/queries/referencias'
import { useSessao } from '../../../auth/contexto'
import { CampoDeSelecao } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'
import type { ItemDeReferencia } from '../../../types/gmud'
import type { FormularioNovaGmud, PropsDosCampos } from './esquema'

/**
 * As opções de uma lista de referência, ou um aviso de espera no lugar delas.
 *
 * Sem o aviso, a lista vazia durante a busca parece cadastro faltando: o usuário abre o campo, vê
 * só "Escolha…" e vai pedir à SSTD que cadastre o sistema dele — que já estava lá.
 */
function OpcoesDeReferencia({ itens, carregando }: { itens: ItemDeReferencia[] | undefined; carregando: boolean }) {
  if (carregando) {
    return <option disabled>{TEXTOS.carregando}</option>
  }
  return (
    <>
      {(itens ?? []).map((item) => (
        <option key={item.id} value={item.id}>
          {item.rotulo}
        </option>
      ))}
    </>
  )
}

/**
 * O que a mudança afeta e quem responde por ela.
 *
 * **Módulo e sub-módulo deixaram de ser texto livre.** Eram, e a justificativa registrada aqui era
 * que "não há tabela de módulos". Havia o dado: nas 1048 solicitações do sistema anterior os dois
 * campos estão preenchidos em todas, e o vocabulário é fechado — 8 módulos e 47 pares. Pedir que
 * alguém leigo em TI adivinhasse a grafia de um módulo do e-Cidade era pedir o impossível, e o
 * resultado seria "Financeiro", "financeiro" e "Finaceiro" contando como três no relatório.
 *
 * O sub-módulo filtra pelo módulo escolhido: dos 47 pares, 15 são do Financeiro e 1 da Assistência
 * Social. Mostrar os 47 de uma vez devolveria ao usuário o problema que a lista veio resolver.
 *
 * Continuam **opcionais** — nenhuma regra depende deles —, e continuam fora de chave estrangeira:
 * as 1027 solicitações migradas trazem o texto do V1, e amarrá-las obrigaria a reescrever
 * histórico.
 *
 * @param props registro do formulário, erros e o controle, para o segundo select acompanhar o
 *     primeiro
 * @returns os campos de sistema, módulo e responsável
 */
export function CamposDoSistema({ register, errors, control }: PropsDosCampos & { control: Control<FormularioNovaGmud> }) {
  const { data: modulos, isPending: carregandoModulos } = useModulos()
  const { data: pessoas, isPending: carregandoPessoas } = usePessoas()
  const moduloEscolhido = useWatch({ control, name: 'modulo' })
  const subModulos = (modulos ?? []).filter((m) => m.subModulo && m.modulo === moduloEscolhido)
  const { data: sistemas, isLoading: carregandoSistemas } = useSistemas()
  const { data: finalidades, isLoading: carregandoFinalidades } = useFinalidades()
  const { usuario } = useSessao()

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <CampoDeSelecao
        rotulo="Sistema afetado"
        ajuda="Onde a mudança será aplicada."
        erro={errors.sistemaId?.message}
        defaultValue=""
        {...register('sistemaId')}
      >
        <option value="" disabled>
          Escolha…
        </option>
        <OpcoesDeReferencia itens={sistemas} carregando={carregandoSistemas} />
      </CampoDeSelecao>

      <CampoDeSelecao
        rotulo="Tipo de manutenção"
        ajuda="A classificação do Termo de Referência para o trabalho que será feito."
        erro={errors.finalidadeId?.message}
        defaultValue=""
        {...register('finalidadeId')}
      >
        <option value="" disabled>
          Escolha…
        </option>
        <OpcoesDeReferencia itens={finalidades} carregando={carregandoFinalidades} />
      </CampoDeSelecao>

      <CampoDeSelecao
        rotulo="Módulo"
        ajuda="A parte do sistema que a mudança altera, se ela for de uma parte só. Opcional."
        erro={errors.modulo?.message}
        defaultValue=""
        {...register('modulo')}
      >
        <option value="">Não se aplica</option>
        {carregandoModulos ? (
          <option disabled>{TEXTOS.carregando}</option>
        ) : (
          (modulos ?? [])
            .filter((m) => !m.subModulo)
            .map((m) => (
              <option key={m.modulo} value={m.modulo}>
                {m.modulo}
              </option>
            ))
        )}
      </CampoDeSelecao>

      <CampoDeSelecao
        rotulo="Sub-módulo"
        // Desabilitado, e não escondido, enquanto não há módulo: um campo que some faz o
        // formulário "pular" ao escolher o módulo, e quem lê a tela de cima a baixo perde o
        // ponto onde estava. Desabilitado ele fica no lugar e explica por que não dá para usar.
        ajuda={
          moduloEscolhido
            ? 'Detalhe do módulo, quando ajuda a localizar a alteração. Opcional.'
            : 'Escolha o módulo primeiro.'
        }
        erro={errors.subModulo?.message}
        defaultValue=""
        disabled={!moduloEscolhido}
        {...register('subModulo')}
      >
        <option value="">Não se aplica</option>
        {subModulos.map((m) => (
          <option key={m.subModulo} value={m.subModulo ?? ''}>
            {m.subModulo}
          </option>
        ))}
      </CampoDeSelecao>

      <CampoDeSelecao
        rotulo="Responsável pela execução"
        // A lista inteira das contas ativas, e não só "eu mesmo". Eram duas opções enquanto não
        // havia endpoint que as listasse — e o efeito era que uma mudança executada pela
        // contratada nascia sem responsável, ou com o solicitante no lugar dele.
        //
        // Quem preencheu vem primeiro e destacado: é a escolha mais comum, e procurá-la no meio
        // de uma lista alfabética é trabalho que a tela pode poupar.
        ajuda="Quem vai executar a mudança. Pode ficar em branco agora e ser definido depois."
        erro={errors.responsavel?.message}
        {...register('responsavel')}
      >
        <option value="">Ainda não definido</option>
        {usuario && <option value={String(usuario.id)}>Eu mesmo — {usuario.nome}</option>}
        {carregandoPessoas ? (
          <option disabled>{TEXTOS.carregando}</option>
        ) : (
          (pessoas ?? [])
            .filter((pessoa) => pessoa.id !== usuario?.id)
            .map((pessoa) => (
              <option key={pessoa.id} value={String(pessoa.id)}>
                {pessoa.nome}
              </option>
            ))
        )}
      </CampoDeSelecao>
    </div>
  )
}
